import { Sha256 } from "./sha256";

/** 8 MiB chunks: coarse enough for few requests, fine enough to resume cheaply. */
export const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024;

export interface ChunkCache {
  get(key: string, index: number): Promise<Uint8Array | undefined>;
  put(key: string, index: number, data: Uint8Array): Promise<void>;
  has(key: string): Promise<number[]>;
  clear(key: string): Promise<void>;
}

export class MemoryChunkCache implements ChunkCache {
  readonly store = new Map<string, Map<number, Uint8Array>>();
  get(key: string, index: number): Promise<Uint8Array | undefined> {
    return Promise.resolve(this.store.get(key)?.get(index));
  }
  put(key: string, index: number, data: Uint8Array): Promise<void> {
    let m = this.store.get(key);
    if (!m) {
      m = new Map<number, Uint8Array>();
      this.store.set(key, m);
    }
    m.set(index, data);
    return Promise.resolve();
  }
  has(key: string): Promise<number[]> {
    return Promise.resolve([...(this.store.get(key)?.keys() ?? [])].sort((a, b) => a - b));
  }
  clear(key: string): Promise<void> {
    this.store.delete(key);
    return Promise.resolve();
  }
}

const DB_NAME = "qh-installer";
const STORE = "chunks";

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error("IndexedDB error"));
  });
}

/** IndexedDB-backed chunk cache; survives reloads so a 2 GB download resumes. */
export class IdbChunkCache implements ChunkCache {
  private dbp: Promise<IDBDatabase> | undefined;

  constructor(private readonly factory: IDBFactory = indexedDB) {}

  private db(): Promise<IDBDatabase> {
    if (!this.dbp) {
      this.dbp = new Promise((resolve, reject) => {
        const open = this.factory.open(DB_NAME, 1);
        open.onupgradeneeded = () => {
          const db = open.result;
          if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: ["key", "index"] });
        };
        open.onsuccess = () => resolve(open.result);
        open.onerror = () => reject(open.error ?? new Error("IndexedDB open failed"));
      });
    }
    return this.dbp;
  }

  async get(key: string, index: number): Promise<Uint8Array | undefined> {
    const db = await this.db();
    const tx = db.transaction(STORE, "readonly");
    const row = (await req(tx.objectStore(STORE).get([key, index]))) as { data: ArrayBuffer } | undefined;
    return row ? new Uint8Array(row.data) : undefined;
  }

  async put(key: string, index: number, data: Uint8Array): Promise<void> {
    const db = await this.db();
    const tx = db.transaction(STORE, "readwrite");
    const copy = data.slice().buffer;
    await req(tx.objectStore(STORE).put({ key, index, data: copy }));
  }

  async has(key: string): Promise<number[]> {
    const db = await this.db();
    const tx = db.transaction(STORE, "readonly");
    const range = IDBKeyRange.bound([key, 0], [key, Number.MAX_SAFE_INTEGER]);
    const keys = (await req(tx.objectStore(STORE).getAllKeys(range))) as [string, number][];
    return keys.map((k) => k[1]).sort((a, b) => a - b);
  }

  async clear(key: string): Promise<void> {
    const db = await this.db();
    const tx = db.transaction(STORE, "readwrite");
    const range = IDBKeyRange.bound([key, 0], [key, Number.MAX_SAFE_INTEGER]);
    await req(tx.objectStore(STORE).delete(range));
  }
}

export class DownloadError extends Error {
  constructor(
    readonly code: "http" | "size" | "sha256" | "aborted",
    message: string,
  ) {
    super(message);
    this.name = "DownloadError";
  }
}

export interface DownloadOptions {
  url: string;
  size: number;
  sha256: string;
  /** Cache namespace, e.g. `${build_id}/${file}`. */
  cacheKey: string;
  cache: ChunkCache;
  fetchFn?: typeof fetch;
  chunkSize?: number;
  onProgress?: (doneBytes: number, totalBytes: number) => void;
  signal?: AbortSignal;
}

async function readAll(body: ReadableStream<Uint8Array>, expected: number, signal?: AbortSignal): Promise<Uint8Array> {
  const out = new Uint8Array(expected);
  let got = 0;
  const reader = body.getReader();
  for (;;) {
    if (signal?.aborted) {
      await reader.cancel();
      throw new DownloadError("aborted", "download aborted");
    }
    const { done, value } = await reader.read();
    if (done) break;
    if (got + value.byteLength > expected) throw new DownloadError("size", "server sent more bytes than expected");
    out.set(value, got);
    got += value.byteLength;
  }
  if (got !== expected) throw new DownloadError("size", `short read: ${got}/${expected}`);
  return out;
}

/**
 * Download one file with Range-based resume through a chunk cache, then verify sha256.
 * Returns the whole file in memory (the fastboot layer needs it that way). On a hash
 * mismatch the cache entry is dropped so a retry re-downloads.
 */
export async function downloadFile(o: DownloadOptions): Promise<Uint8Array> {
  const fetchFn = o.fetchFn ?? fetch;
  const chunk = o.chunkSize ?? DEFAULT_CHUNK_SIZE;
  const n = Math.ceil(o.size / chunk);
  const out = new Uint8Array(o.size);
  const cached = new Set(await o.cache.has(o.cacheKey));
  let done = 0;
  const report = () => o.onProgress?.(done, o.size);

  for (let i = 0; i < n; i++) {
    const start = i * chunk;
    const end = Math.min(o.size, start + chunk);
    if (cached.has(i)) {
      const data = await o.cache.get(o.cacheKey, i);
      if (data && data.byteLength === end - start) {
        out.set(data, start);
        done += data.byteLength;
        report();
        continue;
      }
    }
    if (o.signal?.aborted) throw new DownloadError("aborted", "download aborted");
    const res = await fetchFn(o.url, { headers: { Range: `bytes=${start}-${end - 1}` }, signal: o.signal ?? null });
    if (res.status === 206) {
      if (!res.body) throw new DownloadError("http", "no body");
      const data = await readAll(res.body, end - start, o.signal);
      out.set(data, start);
      await o.cache.put(o.cacheKey, i, data);
      done += data.byteLength;
      report();
      continue;
    }
    if (res.status === 200) {
      // Server ignored Range: take the whole body and slice it into the cache.
      if (!res.body) throw new DownloadError("http", "no body");
      const all = await readAll(res.body, o.size, o.signal);
      out.set(all, 0);
      for (let j = 0; j < n; j++) {
        if (!cached.has(j)) await o.cache.put(o.cacheKey, j, all.subarray(j * chunk, Math.min(o.size, (j + 1) * chunk)));
      }
      done = o.size;
      report();
      break;
    }
    throw new DownloadError("http", `HTTP ${res.status} fetching ${o.url}`);
  }

  const h = new Sha256();
  for (let off = 0; off < out.byteLength; off += chunk) h.update(out.subarray(off, Math.min(out.byteLength, off + chunk)));
  const got = h.hex();
  if (got !== o.sha256) {
    await o.cache.clear(o.cacheKey);
    throw new DownloadError("sha256", `sha256 mismatch for ${o.url}: expected ${o.sha256}, got ${got}`);
  }
  return out;
}
